import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";

// Düğme sistemi (docs/design-system.md → Button). Tüm boyutlar ≥ 44 px dokunma alanı.
//  cta       Fly Orange (erişilebilir ton, beyaz etiket ≥ 4.5:1): sayfada tek birincil eylem (Tur ara, Acenteni bul)
//  primary   Express Teal dolgu: ikincil ama önemli eylem (Acente girişi)
//  secondary Beyaz zemin + teal kenarlık
//  ghost     Zeminsiz, üzerine gelince açık teal
//  link      Metin bağlantısı görünümü (altı çizili)
//  onDark    Kurumsal (teal) zeminde beyaz kenarlıklı
// Durumlar: hover, active (bir ton koyu), focus-visible (globals.css halka), disabled/aria-disabled (soluk, tıklanamaz),
// yükleniyor (aria-busy + dönen simge; metin değişir, düğme genişliği korunur).
type Variant = "cta" | "primary" | "secondary" | "ghost" | "link" | "onDark";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  cta: "bg-cta text-on-cta shadow-sm hover:bg-cta-hover active:bg-cta-active",
  primary: "bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-hover",
  secondary: "bg-surface text-ink ring-1 ring-inset ring-line-strong hover:bg-primary-soft hover:ring-ink active:bg-primary-soft",
  ghost: "text-ink hover:bg-primary-soft active:bg-primary-soft",
  link: "h-auto! px-0! text-ink underline decoration-accent decoration-2 underline-offset-4 hover:text-cta-text",
  onDark: "text-on-primary ring-1 ring-inset ring-on-primary/70 hover:bg-on-primary/10 active:bg-on-primary/15",
};

const sizes: Record<Size, string> = {
  sm: "h-11 px-4 text-sm gap-1.5",
  md: "h-12 px-5 text-[0.95rem] gap-2",
  lg: "h-14 px-7 text-base gap-2.5",
};

export function buttonClass(variant: Variant = "cta", size: Size = "md", extra = "") {
  return [
    "inline-flex items-center justify-center rounded-md font-semibold whitespace-nowrap select-none",
    "transition-[background-color,color,box-shadow] duration-150",
    "disabled:pointer-events-none disabled:opacity-55 aria-disabled:pointer-events-none aria-disabled:opacity-55",
    variants[variant],
    sizes[size],
    extra,
  ].join(" ");
}

type Common = { variant?: Variant; size?: Size; className?: string; children: ReactNode };

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="animate-spin motion-reduce:animate-none">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Button({ variant, size, className, loading, children, ...props }: Common & ComponentProps<"button"> & { loading?: boolean }) {
  return (
    <button className={buttonClass(variant, size, className)} aria-busy={loading || undefined} {...props}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function ButtonLink({
  variant,
  size,
  className,
  external,
  ...props
}: Common & ComponentProps<typeof Link> & { external?: boolean }) {
  if (external) {
    const { href, ...rest } = props;
    return (
      <a
        href={String(href)}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClass(variant, size, className)}
        {...(rest as ComponentProps<"a">)}
      />
    );
  }
  return <Link className={buttonClass(variant, size, className)} {...props} />;
}
